import { Stack } from '@mui/material';
import { PLAN_STATUSES } from '@vy/types';
import { Optional, vySerialize } from '@vy/utils';
import React, { ChangeEvent, useMemo } from 'react';
import { AutocompleteSelect } from '../../../../../../../components/design/AutoCompleteSelect';
import { VyRadio, VyRadioGroup } from '../../../../../../../components/new-design/inputs/VyRadio';
import { usePlansOverviews } from '../../../../../../../hooks/requests/use-plans-overview/use-plans-overviews';
import { PLAN_SELECTION_OPTIONS, TEMPLATE_SELECT_FIELD_SX } from './plan-selection-dialog-consts';
import type { PlanSelectionOption } from './plan-selection-dialog-types';

type PlanSelectionDialogContentProps = {
  selectedOption: Optional<PlanSelectionOption>;
  onOptionSelect: (option: PlanSelectionOption) => void;
  selectedPlanId: Optional<string>;
  onPlanSelect: (planId: string | null) => void;
};

export const PlanSelectionDialogContent: React.FC<PlanSelectionDialogContentProps> = ({
  selectedOption,
  onOptionSelect,
  selectedPlanId,
  onPlanSelect,
}) => {
  const { data } = usePlansOverviews({
    page: 0,
    pageSize: 200,
    planStatuses: vySerialize([PLAN_STATUSES.Active]),
  });

  const plans = useMemo(() => data?.plansOverviews ?? [], [data]);

  const showTemplateSelect = selectedOption === 'existing';

  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    onOptionSelect(event.target.value as PlanSelectionOption);
  };

  return (
    <Stack spacing={3}>
      <VyRadioGroup
        value={selectedOption ?? ''}
        onChange={handleOptionChange}
        options={PLAN_SELECTION_OPTIONS}
        horizontal
        renderOption={(item, index, isActive) => (
          <VyRadio
            key={index}
            value={item.value}
            label={item.label}
            labelSx={{
              '& .MuiFormControlLabel-label .MuiTypography-h6': { fontSize: '14px', fontWeight: 600 },
              '& .MuiFormControlLabel-label .MuiTypography-body2': {
                fontSize: '12px',
                fontWeight: 400,
                maxWidth: '168px',
              },
            }}
            cardMode
            selected={isActive}
            baseLineAlignment
          />
        )}
      />

      <Stack
        sx={{
          opacity: showTemplateSelect ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: showTemplateSelect ? 'auto' : 'none',
        }}
      >
        <AutocompleteSelect
          options={plans}
          getOptionLabel={(option) => option.name}
          textFieldProps={{
            placeholder: 'Select a template',
            sx: TEMPLATE_SELECT_FIELD_SX,
          }}
          onChange={(_, plan) => onPlanSelect(plan?.id ?? null)}
          value={selectedPlanId ? (plans.find((plan) => plan.id === selectedPlanId) ?? null) : null}
        />
      </Stack>
    </Stack>
  );
};
